import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { SortDescriptor, SortOrder } from '../sort/sort';
import { Checkbox } from '../wrappers/va-checkbox/va-checkbox';
import { ExpandView } from '../wrappers/va-expand/va-expand.interface';
import { ColumnDescriptor, TableViewModel } from './table';

/**
 * @deprecated Use `VaFilteredMatTableComponent`
 */
@Component({
  selector: 'va-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./table.component.scss'],
  template: `
    <table
      *ngIf="tableViewModel"
      [style.table-layout]="fixedLayout ? 'fixed' : null"
      [ngClass]="sorting ? 'faded' : 'unfaded'"
    >
      <colgroup *ngIf="fixedLayout">
        <ng-container *ngFor="let column of columns$ | async">
          <col *ngIf="!column.hidden" [style.width]="column.config ? column.config.width : null" />
        </ng-container>
      </colgroup>

      <thead>
        <tr>
          <th *ngIf="tableViewModel.selection && tableViewModel.selection.selectAll">
            <va-checkbox [config]="selectAllCheckbox"></va-checkbox>
          </th>
          <ng-container *ngFor="let column of columns$ | async">
            <th
              *ngIf="!column.hidden"
              [style.text-align]="column.alignment"
              [style.width]="column.config ? column.config.width : null"
              [style.min-width]="column.config! | getMinWidth"
              [style.max-width]="column.config! | getMaxWidth"
            >
              <span
                class="header-title"
                (click)="columnHeaderClicked(column)"
                [style.cursor]="column.sortOn ? 'pointer' : 'unset'"
                [style.justify-content]="column | textDirection"
              >
                {{ column.title | translate }}
                <mat-icon
                  *ngIf="column.sortOn"
                  class="sort-icon"
                  [ngClass]="{ sorted: column | isSortedProperty : sort }"
                >
                  {{ column.sortOn | getSortingIndicatorClass : sort }}
                </mat-icon>
              </span>
            </th>
          </ng-container>
        </tr>
      </thead>

      <tbody>
        <ng-container *ngFor="let item of items; let i = index">
          <tr class="gray-on-hover" [ngClass]="item | rowStatusCss : tableViewModel">
            <td *ngIf="tableViewModel.selection && tableViewModel.selection.selectable">
              <va-checkbox [config]="checkboxFor(item)"></va-checkbox>
            </td>
            <ng-container *ngFor="let column of columns$ | async">
              <td *ngIf="!column.hidden" [style.text-align]="column.alignment">
                <ng-container *ngIf="column.cellForItem(item) as cell">
                  <label *ngIf="cell | asTextCell as textCell">{{ textCell | translate }}</label>
                  <ng-container vaCell *ngIf="cell | asObjectCell as objectCell" [cell]="objectCell"></ng-container>
                </ng-container>
              </td>
            </ng-container>
            <td *ngIf="expandRowView" [style.text-align]="'right'" [style.width.px]="44">
              <button mat-icon-button (click)="toggleExpandViewVisible(i)">
                <mat-icon>{{ getExpandButtonIcon(i) }}</mat-icon>
              </button>
            </td>
          </tr>

          <tr *ngIf="expandRowView && expandViewVisible(i)">
            <td [colSpan]="(columns$ | async)!.length + numberOfAdditionalColumns">
              <ng-container expandedView [item]="item" [view]="expandRowView"></ng-container>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>

    <div class="table-footer stencil-shimmer">
      <ng-container *ngIf="loading">
        <div *ngFor="let row of [0, 1, 2]" class="stencil-shimmer loading-row">
          <span *ngFor="let column of columns$ | async" class="loading-col">
            <div class="loading-item"></div>
          </span>
        </div>
        <div class="shine"></div>
      </ng-container>
    </div>

    <div class="table-footer">
      <div *ngIf="empty && !loading" class="table-empty">{{ tableViewModel.emptyText }}</div>
      <button mat-button color="primary" *ngIf="showLoadMore && !empty && !loadingMore" (click)="loadMoreClicked()">
        LOAD MORE
      </button>
      <mat-spinner [diameter]="24" [strokeWidth]="4" *ngIf="loadingMore && !empty"></mat-spinner>
    </div>
  `,
})
export class TableComponent<T> implements OnInit {
  @Input() items: T[];
  @Input() tableViewModel: TableViewModel<T>;
  @Input() fixedLayout = false;
  @Input() expandRowView: ExpandView<T>;
  @Input() sort: SortDescriptor;
  @Input() showLoadMore: boolean;
  @Input() loadingMore: boolean;
  @Input() sorting: boolean;
  @Output() sortChanged = new EventEmitter<SortDescriptor>();

  private expandViewVisbleIndex = -1;

  columns$: Observable<ColumnDescriptor<T>[]>;

  get numberOfAdditionalColumns(): number {
    let index = 0;
    if (this.tableViewModel.selection) {
      index++;
    }
    if (this.expandRowView) {
      index++;
    }
    return index;
  }

  get loading(): boolean {
    return !this.items;
  }

  get empty(): boolean {
    return this.items ? this.items.length === 0 : true;
  }

  loadMoreClicked(): void {
    if (this.tableViewModel.loadMoreItemsTriggered) {
      this.tableViewModel.loadMoreItemsTriggered();
    }
  }

  ngOnInit(): void {
    if (this.tableViewModel) {
      this.columns$ = this.tableViewModel.select('columns');
    }
  }

  getExpandButtonIcon(index: number): string {
    return index === this.expandViewVisbleIndex ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
  }

  expandViewVisible(index: number): boolean {
    return index === this.expandViewVisbleIndex;
  }

  /*
   * Sort
   */

  public columnHeaderClicked(column: ColumnDescriptor<T>): void {
    if (column.sortOn) {
      this.sortOnProperty(column.sortOn);
      this.sortChanged.emit(this.sort);
    }
  }

  private sortOnProperty(property: string): void {
    if (this.sort) {
      if (this.sort.fieldname === property) {
        this.sort.order = this.sort.order === SortOrder.Ascending ? SortOrder.Descending : SortOrder.Ascending;
      } else {
        this.sort.fieldname = property;
        this.sort.order = SortOrder.Ascending;
      }
    }
  }

  /*
   * Selection
   */
  get selectAllCheckbox(): Checkbox {
    if (
      !this.tableViewModel ||
      !this.tableViewModel.selection ||
      !this.tableViewModel.selection.selectedItems ||
      !this.items
    ) {
      return {
        onClick: (checked) => this.toggleSelectAll(checked),
      };
    }
    const numSelected = this.tableViewModel.selection.selectedItems.length;
    const numTotal = this.items.length;
    return {
      onClick: (checked) => this.toggleSelectAll(checked),
      checked: numSelected === numTotal,
      indeterminate: numSelected > 0 && numSelected < numTotal,
    };
  }

  checkboxFor(item: T): Checkbox {
    return {
      disabled: false,
      onClick: () => this.select(item),
      checked: this.isSelected(item),
      indeterminate: false,
    };
  }

  public toggleExpandViewVisible(index: number): void {
    if (this.expandViewVisbleIndex === index) {
      this.expandViewVisbleIndex = -1;
    } else {
      this.expandViewVisbleIndex = index;
    }
  }

  private isSelected(item: T): boolean {
    if (
      this.tableViewModel.selection &&
      this.tableViewModel.selection.selectedItems &&
      this.tableViewModel.selection.selectedItems.length > 0
    ) {
      return this.tableViewModel.selection.selectedItems.indexOf(item) > -1 ? true : false;
    }
    return false;
  }

  private toggleSelectAll(checked: boolean): void {
    if (checked) {
      this.tableViewModel.selection!.selectedItems = [...this.items];
    } else {
      this.tableViewModel.selection!.selectedItems = [];
    }
    this.selectionChanged();
  }

  private select(item: T): void {
    if (!this.tableViewModel.selection!.selectedItems) {
      this.tableViewModel.selection!.selectedItems = [];
    }
    const index = this.tableViewModel.selection!.selectedItems.indexOf(item);
    if (index > -1) {
      this.tableViewModel.selection!.selectedItems.splice(index, 1);
    } else {
      this.tableViewModel.selection!.selectedItems.push(item);
    }
    this.selectionChanged();
  }

  private selectionChanged(): void {
    if (this.tableViewModel.selection!.selectionChanged) {
      this.tableViewModel.selection!.selectionChanged([...this.tableViewModel.selection!.selectedItems!]);
    }
  }
}
